import { useEffect, useRef, useState, type RefObject } from 'react';

/** How far the finger has to pull, after damping, to trigger a refresh. */
const THRESHOLD_PX = 72;
/** The indicator stops following the finger here. */
const MAX_PX = 112;
/** Finger travel per pixel of indicator travel: a pull should feel heavy. */
const DAMPING = 0.5;
/** Movement below this is a tap that wobbled, not a gesture. */
const SLOP_PX = 6;
/** Indicator size, so it can start fully hidden above the content. */
const INDICATOR_PX = 40;

/**
 * Pull-to-refresh on the shell's scroll container, for touch screens.
 *
 * An installed app has no browser reload button, and the app turns off the
 * browser's own pull gesture (overscroll-behavior), so without this a phone
 * had no way to say "show me the latest" short of closing the app.
 *
 * Material-style: the page itself does not move; a round indicator follows
 * the finger down and spins while `onRefresh` runs. Listeners are passive and
 * the indicator is moved by writing its style directly, so a pull never
 * blocks scrolling and never re-renders the page per frame.
 *
 * A pull only starts at the very top of the page, outside any inner scroller
 * that is itself scrolled, and only when the gesture is clearly vertical, so
 * the week strip's sideways swipe and a scrolled panel are left alone.
 */
export function usePullToRefresh({
  scrollRef,
  indicatorRef,
  enabled,
  onRefresh,
}: {
  scrollRef: RefObject<HTMLElement | null>;
  indicatorRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  onRefresh: () => Promise<unknown>;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  });

  useEffect(() => {
    const scroller = scrollRef.current;
    const indicator = indicatorRef.current;
    if (!enabled || !scroller || !indicator) return;
    if (!window.matchMedia('(pointer: coarse)').matches) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let decided = false;
    let armed = false;
    let busy = false;
    let distance = 0;

    const paint = (d: number, animate: boolean) => {
      const progress = Math.min(1, d / THRESHOLD_PX);
      indicator.style.transition = animate
        ? 'transform 200ms ease-out, opacity 200ms ease-out'
        : 'none';
      indicator.style.transform = `translate3d(0, ${d - INDICATOR_PX}px, 0)`;
      indicator.style.opacity = String(progress);
      indicator.style.setProperty('--pull', String(progress));
    };

    const insideScrolledPanel = (target: EventTarget | null) => {
      for (
        let el = target instanceof Element ? target : null;
        el && el !== scroller;
        el = el.parentElement
      ) {
        if (el.scrollTop > 0) return true;
      }
      return false;
    };

    const reset = () => {
      tracking = false;
      decided = false;
      armed = false;
      distance = 0;
    };

    const onStart = (e: TouchEvent) => {
      if (
        busy ||
        e.touches.length !== 1 ||
        scroller.scrollTop > 0 ||
        insideScrolledPanel(e.target)
      ) {
        reset();
        return;
      }
      tracking = true;
      decided = false;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };

    const onMove = (e: TouchEvent) => {
      if (!tracking) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (!decided) {
        if (Math.abs(dx) < SLOP_PX && Math.abs(dy) < SLOP_PX) return;
        decided = true;
        const pullingDown =
          dy > 0 &&
          Math.abs(dy) > Math.abs(dx) * 1.5 &&
          scroller.scrollTop <= 0;
        if (!pullingDown) {
          reset();
          return;
        }
      }
      distance = Math.min(MAX_PX, Math.max(0, dy * DAMPING));
      const nowArmed = distance >= THRESHOLD_PX;
      // A tick when the pull crosses the line, as native pull-to-refresh
      // does. Android only; iOS has no vibration API and ignores the call.
      if (nowArmed && !armed) navigator.vibrate?.(8);
      armed = nowArmed;
      paint(distance, false);
    };

    const onEnd = () => {
      if (!tracking || !decided) {
        reset();
        return;
      }
      const trigger = armed;
      reset();
      if (!trigger) {
        paint(0, true);
        return;
      }
      busy = true;
      setRefreshing(true);
      paint(THRESHOLD_PX * 0.75, true);
      void onRefreshRef.current().finally(() => {
        busy = false;
        setRefreshing(false);
        paint(0, true);
      });
    };

    paint(0, false);
    scroller.addEventListener('touchstart', onStart, { passive: true });
    scroller.addEventListener('touchmove', onMove, { passive: true });
    scroller.addEventListener('touchend', onEnd, { passive: true });
    scroller.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      scroller.removeEventListener('touchstart', onStart);
      scroller.removeEventListener('touchmove', onMove);
      scroller.removeEventListener('touchend', onEnd);
      scroller.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, scrollRef, indicatorRef]);

  return refreshing;
}
